import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Direction, GameState } from './types';
import { initializeGame, move, getHighestTileValue } from './gameLogic';
import { useTouchControls } from './hooks/useTouchControls';
import { useAudio } from './hooks/useAudio';
import { useReplayRecorder } from './hooks/useReplayRecorder';
import { useAuthContext } from '../../contexts/AuthContext';
import GameBoard from './GameBoard';
import GameOverModal from './GameOverModal';
import EndGameModal from './EndGameModal';
import PlayerInfo from '../shared/PlayerInfo';
import { sendTelemetry } from '../../utils/telemetry';
import './Alien2048.css';

// Generate UUID v4
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Generate a random display name for anonymous players
function generateRandomName(): string {
  const adjectives = ['Cosmic', 'Stellar', 'Astral', 'Lunar', 'Solar', 'Nebula'];
  const nouns = ['Voyager', 'Explorer', 'Pilot', 'Captain', 'Navigator', 'Ranger'];
  const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = nouns[Math.floor(Math.random() * nouns.length)];
  const num = Math.floor(Math.random() * 999);
  return `${adj}${noun}${num}`;
}

/**
 * Alien2048 - Main game component
 *
 * Auth is handled internally via useAuthContext hook
 */
const Alien2048: React.FC = () => {
  const navigate = useNavigate();

  // Get auth state from new auth system
  const { isAuthenticated, user } = useAuthContext();

  // Get user ID and display name from new auth system
  const userId = isAuthenticated && user ? user.id : null;
  const authDisplayName = isAuthenticated && user ? user.displayName : null;

  // Check for preset name: authenticated user's displayName > sessionStorage > random
  const storedPlayerName = sessionStorage.getItem('alien2048_playerName');
  const presetName = authDisplayName || storedPlayerName || generateRandomName();

  // Development mode: parse ?board=2,4,8,16,... query parameter to seed board
  // Must specify all 16 tiles (use 0 for empty cells)
  const parseSeedTiles = (): number[] | undefined => {
    const params = new URLSearchParams(window.location.search);
    const boardParam = params.get('board');

    if (!boardParam) return undefined;

    // Only allow in development or test environments, or when explicitly running tests
    const isDevOrTest = process.env.NODE_ENV === 'development' ||
      process.env.NODE_ENV === 'test' ||
      window.location.hostname === 'localhost';

    if (!isDevOrTest) {
      console.warn('🚫 Board seeding is only available in development/test environments');
      return undefined;
    }

    const tiles = boardParam.split(',').map(v => parseInt(v.trim(), 10));

    // Must have exactly 16 values (4x4 grid)
    if (tiles.length !== 16) {
      console.error('❌ Board parameter must have exactly 16 values (got', tiles.length, ')');
      return undefined;
    }

    // Check all values are valid numbers
    if (tiles.some(v => isNaN(v) || v < 0)) {
      console.error('❌ Board parameter contains invalid values');
      return undefined;
    }

    console.log('🎮 Development mode: Seeding board with:', tiles);
    return tiles;
  };

  const [gameState, setGameState] = useState<GameState>(() => initializeGame(parseSeedTiles()));
  const [showGameOver, setShowGameOver] = useState(false);

  // Generate random positions for space elements on mount
  const spaceElements = useMemo(() => {
    const rand = (min: number, max: number) => Math.random() * (max - min) + min;

    // Twinkling stars - random positions across the screen
    const twinkleStars = Array.from({ length: 11 }, (_, i) => ({
      top: `${rand(5, 90)}%`,
      left: `${rand(5, 90)}%`,
      size: rand(2, 4),
      duration: rand(4.5, 7),
      delay: rand(0, 8),
    }));

    // Nebula clouds - spread across different areas
    const nebulas = [
      { top: `${rand(5, 25)}%`, left: `${rand(-10, 15)}%`, width: rand(250, 350), height: rand(150, 220), hue: 270 }, // purple
      { top: `${rand(50, 70)}%`, right: `${rand(-15, 10)}%`, width: rand(200, 300), height: rand(140, 200), hue: 330 }, // pink
      { bottom: `${rand(0, 15)}%`, left: `${rand(15, 35)}%`, width: rand(280, 400), height: rand(180, 250), hue: 220 }, // blue
      { top: `${rand(30, 50)}%`, right: `${rand(0, 20)}%`, width: rand(180, 250), height: rand(120, 180), hue: 280 }, // violet
    ];

    // Distant galaxies - small elliptical shapes
    const galaxies = [
      { top: `${rand(10, 25)}%`, right: `${rand(15, 35)}%`, rotation: rand(-45, 45) },
      { top: `${rand(40, 55)}%`, left: `${rand(10, 25)}%`, rotation: rand(-45, 45) },
      { bottom: `${rand(20, 35)}%`, right: `${rand(5, 20)}%`, rotation: rand(-45, 45) },
    ];

    // Shooting stars - random starting positions and angles
    const shootingStars = [
      { top: `${rand(0, 20)}%`, left: `${rand(5, 25)}%`, angle: rand(50, 60), duration: rand(7, 10), delay: 0 },
      { top: `${rand(25, 45)}%`, left: `${rand(50, 75)}%`, angle: rand(30, 40), duration: rand(10, 14), delay: rand(2, 5) },
      { top: `${rand(55, 75)}%`, left: `${rand(10, 30)}%`, angle: rand(45, 55), duration: rand(8, 12), delay: rand(5, 9) },
      { top: `${rand(15, 30)}%`, left: `${rand(65, 85)}%`, angle: rand(55, 65), duration: rand(12, 18), delay: rand(3, 7) },
      { top: `${rand(40, 55)}%`, left: `${rand(0, 15)}%`, angle: rand(25, 35), duration: rand(15, 20), delay: rand(8, 12) },
    ];

    return { twinkleStars, nebulas, galaxies, shootingStars };
  }, []);
  const [showWinBanner, setShowWinBanner] = useState(false);
  const [showEndGameModal, setShowEndGameModal] = useState(false);
  // Never show modal - always auto-start with preset name
  const [isNameModalOpen, setNameModalOpen] = useState(false);
  const [name, setName] = useState<string | null>(presetName);
  const [autoStartTriggered, setAutoStartTriggered] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'saving' | 'success' | 'error' | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showDirectionArrow, setShowDirectionArrow] = useState<Direction | null>(null);
  const { playMoveSound, playMergeSound, playInvalidSound, startMusic, stopMusic, isMusicMuted, isSoundMuted, toggleMusicMute, toggleSoundMute } = useAudio();
  // Note: Score submission removed - scores are created through replay finalization

  // Replay recording
  const gamePlayUUID = useRef<string>(generateUUID());
  const {
    createReplaySession,
    recordMove,
    recordBoardState,
    recordScoreUpdate,
    recordGameState,
    updateSession,
    finalizeReplay,
    isRecorderReady
  } = useReplayRecorder(gamePlayUUID.current, userId);

  const gameStartTime = useRef<number>(0);

  // Check if game starts in game over state (for seeded boards with no moves)
  useEffect(() => {
    if (gameState.gameOver && !isNameModalOpen) {
      setTimeout(() => setShowGameOver(true), 500);
    }
  }, [gameState.gameOver, isNameModalOpen]);

  // Prevent scrolling during gameplay
  useEffect(() => {
    document.body.classList.add('gameplay-page');

    // Update theme color for PWA status bar to match dark space background
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    const originalThemeColor = themeColorMeta?.getAttribute('content') || '#D88A3C';
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', '#050814');
    }

    // Fix PWA safe area: make html cover full screen including safe areas
    // Only apply explicit height in PWA mode to avoid scroll issues in regular browser
    const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                  (window.navigator as any).standalone === true;
    const originalHtmlHeight = document.documentElement.style.height;
    const originalHtmlMinHeight = document.documentElement.style.minHeight;
    const originalHtmlBg = document.documentElement.style.backgroundColor;
    const originalHtmlOverflow = document.documentElement.style.overflow;

    if (isPWA) {
      const fullScreenHeight = Math.max(window.outerHeight, window.screen.height, window.innerHeight);
      document.documentElement.style.height = `${fullScreenHeight}px`;
      document.documentElement.style.minHeight = `${fullScreenHeight}px`;
    }
    // Always set overflow hidden on html to prevent iOS Safari rubber-banding
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.backgroundColor = '#050814';

    // Disable pinch-to-zoom on game pages
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    const originalViewport = viewportMeta?.getAttribute('content') || '';
    if (viewportMeta) {
      viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover');
    }

    // iOS ignores user-scalable=no, so we need to prevent gesture events
    const preventZoom = (e: TouchEvent) => {
      if (e.touches && e.touches.length > 1) {
        e.preventDefault();
      }
    };
    const preventGestureZoom = (e: Event) => {
      e.preventDefault();
    };
    document.addEventListener('touchmove', preventZoom, { passive: false });
    document.addEventListener('gesturestart', preventGestureZoom);
    document.addEventListener('gesturechange', preventGestureZoom);
    document.addEventListener('gestureend', preventGestureZoom);

    return () => {
      document.body.classList.remove('gameplay-page');
      stopMusic(); // Stop music when leaving the game

      // Restore original theme color
      if (themeColorMeta) {
        themeColorMeta.setAttribute('content', originalThemeColor);
      }

      // Restore original html styles
      document.documentElement.style.height = originalHtmlHeight;
      document.documentElement.style.minHeight = originalHtmlMinHeight;
      document.documentElement.style.backgroundColor = originalHtmlBg;
      document.documentElement.style.overflow = originalHtmlOverflow;

      // Restore original viewport
      if (viewportMeta && originalViewport) {
        viewportMeta.setAttribute('content', originalViewport);
      }

      // Remove zoom prevention listeners
      document.removeEventListener('touchmove', preventZoom);
      document.removeEventListener('gesturestart', preventGestureZoom);
      document.removeEventListener('gesturechange', preventGestureZoom);
      document.removeEventListener('gestureend', preventGestureZoom);
    };
  }, [stopMusic]);

  // Start music when the game begins (after name modal closes)
  useEffect(() => {
    if (!isNameModalOpen) {
      startMusic();
      // Send telemetry after modal closes so we capture actual game layout
      sendTelemetry('Alien2048');
    }
  }, [isNameModalOpen, startMusic]);

  const handleNameConfirmation = useCallback(async (playerName: string | null) => {
    setName(playerName);
    setNameModalOpen(false);

    // Start tracking game time (no score submission needed)
    gameStartTime.current = Date.now();

    // Create replay session
    await createReplaySession(playerName, gameState.grid);

    // Mark session as in progress
    updateSession({ game_state: 1 }); // GAME_STATE_IN_PROGRESS
  }, [createReplaySession, gameState.grid, updateSession]);

  // Auto-start game immediately (no modal needed)
  useEffect(() => {
    if (!autoStartTriggered && presetName) {
      // Clear sessionStorage so refresh generates a new name
      if (storedPlayerName) {
        sessionStorage.removeItem('alien2048_playerName');
      }
      setAutoStartTriggered(true);
      // Start game with the preset name
      handleNameConfirmation(presetName);
    }
  }, [autoStartTriggered, presetName, storedPlayerName, handleNameConfirmation]);

  const handleMove = useCallback((direction: Direction) => {
    if (gameState.gameOver || isNameModalOpen) return;

    // Show direction arrow feedback
    setShowDirectionArrow(direction);
    setTimeout(() => setShowDirectionArrow(null), 200);

    setGameState(prevState => {
      const newState = move(prevState, direction);
      const elapsedTime = Date.now() - gameStartTime.current;
      const validMove = newState.grid !== prevState.grid;

      // Record move event
      recordMove(elapsedTime, direction, validMove);

      // Record board state changes if valid move
      if (validMove) {
        const boardChanges: Array<[number, number, number]> = [];
        for (let row = 0; row < newState.grid.length; row++) {
          for (let col = 0; col < newState.grid[row].length; col++) {
            const newCell = newState.grid[row][col];
            const oldCell = prevState.grid[row][col];

            // Record if cell value changed
            if (newCell?.value !== oldCell?.value) {
              boardChanges.push([row, col, newCell?.value || 0]);
            }
          }
        }

        if (boardChanges.length > 0) {
          recordBoardState(elapsedTime, boardChanges, 'move_result');
        }

        // Record score update if score changed
        const scoreGained = newState.score - prevState.score;
        if (scoreGained > 0) {
          recordScoreUpdate(elapsedTime, newState.score, scoreGained, 'merge');
          playMergeSound(); // Tiles merged
        } else {
          playMoveSound(direction); // Just moved
        }
      } else {
        // Invalid move - grid didn't change
        playInvalidSound();
      }

      // Check if game just ended
      if (newState.gameOver && !prevState.gameOver) {
        // Record final game state
        recordGameState(elapsedTime, 2, newState.moveCount, elapsedTime); // GAME_STATE_COMPLETE

        // Finalize replay with highest tile
        const highestTile = getHighestTileValue(newState.grid);
        setSaveStatus('saving');
        finalizeReplay({
          final_score: newState.score,
          final_moves: newState.moveCount,
          game_state: 2, // GAME_STATE_COMPLETE
          duration_ms: elapsedTime,
          final_board: newState.grid,
          highest_tile: highestTile
        }).then((result) => {
          if (result.success && result.score_id) {
            setSaveStatus('success');
          } else {
            setSaveStatus('error');
            setSaveError('Failed to save to leaderboard, please take a screenshot');
          }
        }).catch((error) => {
          setSaveStatus('error');
          setSaveError('Failed to save to leaderboard, please take a screenshot');
          console.error('Error saving score:', error);
        });

        // Score will be created through replay finalization
        // No need to submit directly - backend validates replay and creates score
        setTimeout(() => setShowGameOver(true), 300);
      }

      // Check if just won
      if (newState.won && !prevState.won) {
        setShowWinBanner(true);
        setTimeout(() => setShowWinBanner(false), 3000);
      }

      return newState;
    });
  }, [gameState.gameOver, isNameModalOpen, playMoveSound, playMergeSound, playInvalidSound, name, recordMove, recordBoardState, recordScoreUpdate, recordGameState, finalizeReplay]);

  const { containerRef, handlers } = useTouchControls({
    onMove: handleMove,
    enabled: !showGameOver && !isNameModalOpen
  });

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (showGameOver || isNameModalOpen || showEndGameModal) return;

      const keyMap: Record<string, Direction> = {
        ArrowUp: 'up',
        ArrowDown: 'down',
        ArrowLeft: 'left',
        ArrowRight: 'right',
        w: 'up',
        W: 'up',
        s: 'down',
        S: 'down',
        a: 'left',
        A: 'left',
        d: 'right',
        D: 'right'
      };

      const direction = keyMap[e.key];
      if (direction) {
        e.preventDefault();
        handleMove(direction);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleMove, showGameOver, isNameModalOpen, showEndGameModal]);

  const handleEndGame = useCallback(() => {
    if (gameState.gameOver) return;
    setShowEndGameModal(true);
  }, [gameState.gameOver]);

  const confirmEndGame = useCallback(() => {
    setShowEndGameModal(false);

    const elapsedTime = Date.now() - gameStartTime.current;

    // Record final game state
    recordGameState(elapsedTime, 2, gameState.moveCount, elapsedTime); // GAME_STATE_COMPLETE

    // Finalize replay with highest tile
    const highestTile = getHighestTileValue(gameState.grid);
    setSaveStatus('saving');
    finalizeReplay({
      final_score: gameState.score,
      final_moves: gameState.moveCount,
      game_state: 2, // GAME_STATE_COMPLETE
      duration_ms: elapsedTime,
      final_board: gameState.grid,
      highest_tile: highestTile
    }).then((result) => {
      if (result.success && result.score_id) {
        setSaveStatus('success');
      } else {
        setSaveStatus('error');
        setSaveError('Failed to save to leaderboard, please take a screenshot');
      }
    }).catch((error) => {
      setSaveStatus('error');
      setSaveError('Failed to save to leaderboard, please take a screenshot');
      console.error('Error saving score:', error);
    });

    // Mark game as over - score will be created through replay finalization
    setGameState(prevState => ({ ...prevState, gameOver: true }));
    // No direct score submission - backend validates replay and creates score
    setTimeout(() => setShowGameOver(true), 300);
  }, [gameState, name, recordGameState, finalizeReplay]);

  return (
    <div
      className="alien2048-container"
      ref={containerRef}
      {...handlers}
    >
      {/* Nebula clouds - random positions */}
      {spaceElements.nebulas.map((nebula, i) => (
        <div
          key={`nebula-${i}`}
          className="nebula-cloud"
          style={{
            top: nebula.top,
            left: nebula.left,
            right: nebula.right,
            bottom: nebula.bottom,
            width: `${nebula.width}px`,
            height: `${nebula.height}px`,
            background: `radial-gradient(ellipse, hsl(${nebula.hue} 70% 50% / 40%) 0%, hsl(${nebula.hue} 80% 30% / 20%) 50%, transparent 70%)`,
            animationDelay: `${i * 6}s`,
          }}
        />
      ))}

      {/* Distant galaxies - random positions */}
      {spaceElements.galaxies.map((galaxy, i) => (
        <div
          key={`galaxy-${i}`}
          className="distant-galaxy"
          style={{
            top: galaxy.top,
            left: galaxy.left,
            right: galaxy.right,
            bottom: galaxy.bottom,
            transform: `rotate(${galaxy.rotation}deg)`,
            animationDelay: `${i * 4}s`,
          }}
        />
      ))}

      {/* Twinkling stars - random positions */}
      {spaceElements.twinkleStars.map((star, i) => (
        <div
          key={`twinkle-${i}`}
          className="twinkle-star"
          style={{
            top: star.top,
            left: star.left,
            width: `${star.size}px`,
            height: `${star.size}px`,
            animationDuration: `${star.duration}s`,
            animationDelay: `${star.delay}s`,
          }}
        />
      ))}

      {/* Shooting stars - use CSS classes but randomize timing */}
      <div className="shooting-star shooting-star-1" style={{ animationDelay: `${spaceElements.shootingStars[0]?.delay || 0}s` }} />
      <div className="shooting-star shooting-star-2" style={{ animationDelay: `${spaceElements.shootingStars[1]?.delay || 2}s` }} />
      <div className="shooting-star shooting-star-3" style={{ animationDelay: `${spaceElements.shootingStars[2]?.delay || 5}s` }} />
      <div className="shooting-star shooting-star-4" style={{ animationDelay: `${spaceElements.shootingStars[3]?.delay || 8}s` }} />
      <div className="shooting-star shooting-star-5" style={{ animationDelay: `${spaceElements.shootingStars[4]?.delay || 11}s` }} />

      {/* Additional star field layers for depth and pseudorandom sparkle */}
      <div className="star-field-layer-2"></div>
      <div className="star-field-layer-3"></div>

      <div className="level-title">Alien 2048</div>

      {/* Stats row above the grid */}
      <div className="stats-container">
        <PlayerInfo anonName={name} />
        <div className="score-display">
          score: {gameState.score}
        </div>
        <div className="moves-display">
          moves: {gameState.moveCount}
        </div>
      </div>

      {/* Game board */}
      <div className="game-area">
        <GameBoard grid={gameState.grid} />
        {showDirectionArrow && (
          <div className={`direction-arrow direction-arrow-${showDirectionArrow}`}>
            {showDirectionArrow === 'up' && '↑'}
            {showDirectionArrow === 'down' && '↓'}
            {showDirectionArrow === 'left' && '←'}
            {showDirectionArrow === 'right' && '→'}
          </div>
        )}
      </div>

      {/* Buttons row below the grid */}
      <div className="button-container">
        <button className="back-btn" onClick={() => navigate('/')}>
          ← Back
        </button>
        <button className="mute-btn" onClick={toggleMusicMute} title={isMusicMuted ? "Unmute Music" : "Mute Music"}>
          <span className={isMusicMuted ? 'muted-icon' : ''}>🎵</span>
        </button>
        <button className="mute-btn" onClick={toggleSoundMute} title={isSoundMuted ? "Unmute Sound" : "Mute Sound"}>
          <span className={isSoundMuted ? 'muted-icon' : ''}>🔊</span>
        </button>
        {!gameState.gameOver && (
          <button className="end-game-btn" onClick={handleEndGame}>
            End Game
          </button>
        )}
      </div>

      <div className="instructions">
        <p><strong>How to play:</strong> Swipe to move tiles. Matching tiles merge!</p>
        <p>Combine tiles to create the legendary Alien!</p>
      </div>

      {showWinBanner && (
        <div className="win-banner">
          🎉 You reached 2048! Keep going!
        </div>
      )}

      {showGameOver && (
        <GameOverModal
          score={gameState.score}
          highestTile={getHighestTileValue(gameState.grid)}
          won={gameState.won}
          moveCount={gameState.moveCount}
          onClose={() => setShowGameOver(false)}
          saveStatus={saveStatus}
          saveError={saveError}
        />
      )}

      {showEndGameModal && (
        <EndGameModal
          onConfirm={confirmEndGame}
          onCancel={() => setShowEndGameModal(false)}
        />
      )}
    </div>
  );
};

export default Alien2048;
